import React, { forwardRef, useState } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { Icon } from '../Icon';
import styles from './Checkbox.module.css';

/**
 * Checkbox — a shadcn-style toggle control, reimplemented in Athena's idiom
 * (JSX + CSS Modules + 2.0 tokens). No Radix/Base-UI dependency: renders a
 * `role="checkbox"` button with a check / indeterminate indicator. Styled with
 * `--checkbox-*` tokens (globals.css). Preview-only for now (globals.css isn't
 * imported into the app); not wired into any live feature yet.
 *
 * Props:
 *   checked          — controlled checked state (boolean)
 *   defaultChecked   — uncontrolled initial state (default false)
 *   onCheckedChange  — (next: boolean) => void
 *   indeterminate    — show the mixed (dash) state; aria-checked="mixed"
 *   disabled         — disable interaction
 *   aria-invalid     — render the invalid (destructive) styles
 *   id / name / className / …rest forwarded to the button
 */

const Checkbox = forwardRef(
  (
    {
      checked,
      defaultChecked = false,
      onCheckedChange,
      indeterminate = false,
      disabled = false,
      className = '',
      id,
      name,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const isControlled = checked !== undefined;
    const [internal, setInternal] = useState(defaultChecked);
    const value = isControlled ? checked : internal;

    const toggle = (e) => {
      if (disabled) return;
      props.onClick?.(e);
      const next = !value;
      if (!isControlled) setInternal(next);
      onCheckedChange?.(next);
    };

    return (
      <button
        ref={ref}
        type="button"
        role="checkbox"
        id={id}
        name={name}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : value}
        aria-invalid={ariaInvalid}
        data-checked={value || undefined}
        data-indeterminate={indeterminate || undefined}
        className={`${styles.checkbox} ${className}`}
        {...props}
        onClick={toggle}
      >
        {(value || indeterminate) && (
          <span className={styles.indicator}>
            <Icon name={indeterminate ? 'minus' : 'check'} className={styles.icon} />
          </span>
        )}
      </button>
    );
  }
);

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  'aria-invalid': PropTypes.bool,
  onClick: PropTypes.func,
};

export default Checkbox;
export { Checkbox };
